import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ideas as ideasApi, Idea } from '../lib/api'

type DocType = 'CB' | 'SP' | 'BP'

const DOC_INFO: Record<DocType, { title: string; description: string }> = {
  CB: { title: 'Concept Brief', description: 'What is this idea? Define the core concept, problem space, and vision.' },
  SP: { title: 'Strategic Plan', description: 'Why does it matter? Articulate value proposition, market context, and success metrics.' },
  BP: { title: 'Blueprint', description: 'How will it work? Detail architecture, implementation, resources, and timeline.' },
}

export default function CanonEditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [idea, setIdea] = useState<Idea | null>(null)
  const [canon, setCanon] = useState({ CB: '', SP: '', BP: '' })
  const [activeDoc, setActiveDoc] = useState<DocType>('CB')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  // Goal profile
  const [goalProfile, setGoalProfile] = useState({
    archetype_id: '',
    scope: 'personal',
    intent: 'explore',
    stakes: 'low',
    audience: ['self'] as string[],
  })

  useEffect(() => {
    if (!id) return
    let cancelled = false

    const load = async () => {
      try {
        const data = await ideasApi.get(id)
        if (cancelled) return
        setIdea(data)
        setCanon({
          CB: data.canon?.CB || '',
          SP: data.canon?.SP || '',
          BP: data.canon?.BP || '',
        })
        if (data.goal_profile) {
          setGoalProfile({
            archetype_id: data.goal_profile.archetype_id || '',
            scope: data.goal_profile.scope || 'personal',
            intent: data.goal_profile.intent || 'explore',
            stakes: data.goal_profile.stakes || 'low',
            audience: data.goal_profile.audience || ['self'],
          })
        }
      } catch {
        navigate('/dashboard')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [id, navigate])

  const handleSave = async () => {
    if (!id) return
    setSaving(true)
    try {
      await Promise.all([
        ideasApi.updateCanon(id, canon),
        ideasApi.updateGoalProfile(id, goalProfile),
      ])
      navigate(`/ideas/${id}`)
    } catch (e) {
      console.error('Failed to save:', e)
    } finally {
      setSaving(false)
    }
  }

  const handleGenerate = async () => {
    if (!id) return
    setGenerating(true)
    setGenerateError(null)
    try {
      const result = await ideasApi.generateCanon(id) as { canon?: { CB?: string; SP?: string; BP?: string } }
      if (result?.canon) {
        setCanon((prev) => ({
          CB: result.canon!.CB || prev.CB,
          SP: result.canon!.SP || prev.SP,
          BP: result.canon!.BP || prev.BP,
        }))
      }
    } catch (e) {
      setGenerateError(e instanceof Error ? e.message : 'Generation failed')
    } finally {
      setGenerating(false)
    }
  }

  const toggleAudience = (value: string) => {
    setGoalProfile((prev) => ({
      ...prev,
      audience: prev.audience.includes(value)
        ? prev.audience.filter((a) => a !== value)
        : [...prev.audience, value],
    }))
  }

  const completeness = (() => {
    const filled = [canon.CB, canon.SP, canon.BP].filter((c) => c.trim().length > 50).length
    return Math.round((filled / 3) * 100)
  })()

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
          <p className="mt-3 text-gray-400 text-sm">Loading editor...</p>
        </div>
      </div>
    )
  }

  if (!idea) return null

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-800 flex-shrink-0 bg-gray-900/50">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/ideas/${id}`)}
            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-sm font-semibold text-gray-100">Canon Editor</h1>
            <p className="text-xs text-gray-500">{idea.title}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Completeness */}
          <div className="flex items-center gap-2">
            <div className="w-24 bg-gray-800 rounded-full h-1.5">
              <div
                className="bg-brand-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${completeness}%` }}
              />
            </div>
            <span className="text-xs text-gray-500">{completeness}%</span>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {saving ? 'Saving...' : 'Save & Close'}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Editor panel (left 2/3) */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Doc tabs */}
          <div className="flex border-b border-gray-800">
            {(['CB', 'SP', 'BP'] as DocType[]).map((type) => {
              const hasContent = canon[type].trim().length > 50
              return (
                <button
                  key={type}
                  onClick={() => setActiveDoc(type)}
                  className={`flex-1 px-6 py-3 text-sm font-medium transition flex items-center justify-center gap-2 ${
                    activeDoc === type
                      ? 'text-brand-400 border-b-2 border-brand-500 bg-gray-900/30'
                      : 'text-gray-500 hover:text-gray-300 hover:bg-gray-900/20'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full ${hasContent ? 'bg-emerald-400' : 'bg-gray-600'}`} />
                  {DOC_INFO[type].title}
                </button>
              )
            })}
          </div>

          {/* Doc description */}
          <div className="px-6 py-3 border-b border-gray-800/50">
            <p className="text-xs text-gray-500">{DOC_INFO[activeDoc].description}</p>
          </div>

          {/* Textarea */}
          <textarea
            value={canon[activeDoc]}
            onChange={(e) => setCanon((prev) => ({ ...prev, [activeDoc]: e.target.value }))}
            placeholder={`Write your ${DOC_INFO[activeDoc].title.toLowerCase()} here...\n\nTip: Use the AI Generate button to draft initial content based on your conversations.`}
            className="flex-1 w-full bg-transparent text-gray-200 text-sm p-6 resize-none outline-none placeholder-gray-600 leading-relaxed font-mono"
            spellCheck={false}
          />
        </div>

        {/* Sidebar panel (right 1/3) */}
        <div className="w-80 border-l border-gray-800 flex-shrink-0 overflow-y-auto bg-gray-950/50">
          {/* Goal Profile */}
          <div className="p-5 border-b border-gray-800">
            <h3 className="text-sm font-semibold text-gray-200 mb-4">Goal Profile</h3>
            <div className="space-y-4">
              {/* Scope */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Scope</label>
                <select
                  value={goalProfile.scope}
                  onChange={(e) => setGoalProfile((prev) => ({ ...prev, scope: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 outline-none focus:ring-1 focus:ring-brand-500"
                >
                  <option value="personal">Personal</option>
                  <option value="market">Market</option>
                  <option value="global">Global</option>
                </select>
              </div>

              {/* Intent */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Intent</label>
                <select
                  value={goalProfile.intent}
                  onChange={(e) => setGoalProfile((prev) => ({ ...prev, intent: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 outline-none focus:ring-1 focus:ring-brand-500"
                >
                  <option value="explore">Explore</option>
                  <option value="achieve">Achieve</option>
                  <option value="professionalize">Professionalize</option>
                </select>
              </div>

              {/* Stakes */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Stakes</label>
                <select
                  value={goalProfile.stakes}
                  onChange={(e) => setGoalProfile((prev) => ({ ...prev, stakes: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 outline-none focus:ring-1 focus:ring-brand-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Audience */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Audience</label>
                <div className="space-y-1.5">
                  {['self', 'family', 'team', 'customers', 'investors', 'public'].map((aud) => (
                    <label key={aud} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={goalProfile.audience.includes(aud)}
                        onChange={() => toggleAudience(aud)}
                        className="w-3.5 h-3.5 rounded border-gray-600 bg-gray-800 text-brand-500 focus:ring-brand-500"
                      />
                      <span className="text-xs text-gray-300 capitalize">{aud}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* AI Generation */}
          <div className="p-5 border-b border-gray-800">
            <div className="bg-brand-950/30 border border-brand-900/30 rounded-lg p-4">
              <div className="flex items-start gap-2.5">
                <svg className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-brand-300 mb-1">AI Generation</p>
                  <p className="text-xs text-gray-500 mb-3">
                    Let AI draft initial canon documents based on your conversations and goal profile.
                  </p>
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="w-full px-3 py-2 text-xs font-medium bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {generating ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                        Generate with AI
                      </>
                    )}
                  </button>
                  {generateError && (
                    <p className="text-xs text-red-400 mt-2">{generateError}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Doc Stats */}
          <div className="p-5">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Document Stats</h3>
            <div className="space-y-2">
              {(['CB', 'SP', 'BP'] as DocType[]).map((type) => {
                const content = canon[type]
                const words = content.trim() ? content.trim().split(/\s+/).length : 0
                const lines = content.trim() ? content.trim().split('\n').length : 0
                return (
                  <div key={type} className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">{DOC_INFO[type].title}</span>
                    <span className="text-gray-500">
                      {words > 0 ? `${words} words, ${lines} lines` : 'Empty'}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
